// api/realtimePredictor.ts

import { Debug } from "@/util/debug";
import { RoastingSeries, RoastLog } from "../input/roastingSeriesTypes";
import { BTCurveSimilarityResult, findMostSimilarBTCurve } from "../prediction/knn/BTCurveSimilarity";
import { knnAggregateWeighted } from "../prediction/knn/knnAggregate";
import { KNNDistanceWeights } from "../prediction/knn/knnDistance";
import {
  DEFAULT_SIMILARITY_FEATURE_WEIGHTS,
  RankedProfile,
  rankPastProfiles,
} from "../prediction/knn/pastProfileSimilarity";
import { PastProfile } from "../prediction/knn/pastProfileTypes";
import { extrapolateDropTime } from "../prediction/shortTerm/extrapolate";
import { computeLossSlope, estimateCurrentLoss } from "../prediction/shortTerm/lossSlope";
import { buildCurrentState, buildCurrentStateFromSeries } from "../state/currentState";
import { findDevStartIndex } from "../state/devPhase";

//실시간 예측 API (Reactor Loop용)
/**
 * 실시간 로스팅 중 호출되는 단기 배출 예측 API
 * → 빠르고 결정 중심
 */
const MIN_DEV_ELAPSED_SEC = 15;
const MIN_LOSS_INTERVAL_SEC = 3;
const CONFIDENCE_MIN = 0.35;
const MAX_HISTORY_SEC = 180;

export type SkipReason =
  | "loss_undefiend" // loss 설정 없음
  | "invalid_series" // dryEndTime 없음, 데이터 부족
  | "dev_too_early" // dryEnd 직후
  | "loss_interval_too_short" // loss 계산 간격 부족
  | "loss_interval_too_short_prevEntry" // loss 계산 간격 부족
  | "loss_interval_too_short_deltaTime" // loss 계산 시간 간격 부족
  | "loss_interval_too_short_lossSlope" // loss 계산 간격 부족
  | "loss_not_progressing";

export interface PredictRealtimeDropResult {
  /** drop 까지 남은 시간 (sec) */
  remainingTime: number;

  /** 예측 drop 시점의 BT */
  predictedDropBT: number;

  /** 현재 시점에서의 추정 loss (다음 호출의 prevLoss로 사용) */
  currLoss: number;
  currTime: number;
  confidence: number;

  /** 가장 유사한 과거 프로파일 (예측 근거용) */
  closestProfile?: RankedProfile;
  /** BT 곡선 기준 가장 유사한 과거 로스팅 (UI 비교용) */
  closestSeries?: BTCurveSimilarityResult;

  /** 이번 호출에서 예측을 수행했는지 여부 */
  skipped: boolean;
  /** 예측을 스킵한 이유 (skipped === true 인 경우만) */
  skipReason?: SkipReason;
}

function findPreviousPrediction(
  history: Record<number, PredictRealtimeDropResult>,
  nowTime: number,
): { time: number; result: PredictRealtimeDropResult } | undefined {
  let prevTime = -Infinity;
  let prevResult: PredictRealtimeDropResult | undefined;

  for (const [tStr, res] of Object.entries(history)) {
    const t = Number(tStr);
    if (t < nowTime && t > prevTime && !res.skipped && !Number.isNaN(res.currLoss)) {
      prevTime = t;
      prevResult = res;
    }
  }

  return prevResult ? { time: prevTime, result: prevResult } : undefined;
}

function pruneByTime(history: Record<number, PredictRealtimeDropResult>, time: number) {
  for (const t of Object.keys(history)) {
    if (Number(t) >= time) delete history[Number(t)];
  }
}
function pruneByWindow(history: Record<number, PredictRealtimeDropResult>, nowTime: number) {
  for (const t of Object.keys(history)) {
    if (nowTime - Number(t) > MAX_HISTORY_SEC) {
      delete history[Number(t)];
    }
  }
}
function findValidPreviousPrediction(history: Record<number, PredictRealtimeDropResult>, nowTime: number) {
  let best: { time: number; result: PredictRealtimeDropResult } | undefined;

  for (const [tStr, r] of Object.entries(history)) {
    const t = Number(tStr);
    if (t < nowTime && r.confidence >= CONFIDENCE_MIN) {
      if (!best || t > best.time) {
        best = { time: t, result: r };
      }
    }
  }

  return best;
}
function isUsableForSlope(r: PredictRealtimeDropResult) {
  return r.confidence >= CONFIDENCE_MIN && Number.isFinite(r.currLoss);
}
function computeMultiHistorySlope(
  history: Record<number, PredictRealtimeDropResult>,
  nowTime: number,
  windowSec = 15,
): number | undefined {
  const entries = Object.entries(history)
    .map(([t, r]) => ({ time: Number(t), r }))
    // .filter((e) => e.time < nowTime && nowTime - e.time <= windowSec && isUsableForSlope(e.r))
    .sort((a, b) => a.time - b.time);

  if (entries.length < 2) {
    Debug("computeMultiHistorySlope entries.length : " + entries.length, entries);
    return;
  }

  let weightedSum = 0;
  let weightTotal = 0;

  for (let i = 1; i < entries.length; i++) {
    const dt = entries[i].time - entries[i - 1].time;
    if (dt <= 0) continue;

    const slope = (entries[i].r.currLoss - entries[i - 1].r.currLoss) / dt;

    if (slope <= 0) continue;

    // 최신 slope일수록 가중치 ↑
    const weight = i / entries.length;

    weightedSum += slope * weight;
    weightTotal += weight;
  }
  Debug("computeMultiHistorySlope weightTotal : " + weightTotal);

  return weightTotal > 0 ? weightedSum / weightTotal : undefined;
}

function skippedResult(reason: SkipReason): PredictRealtimeDropResult {
  return {
    remainingTime: Infinity,
    predictedDropBT: NaN,
    currLoss: NaN,
    currTime: NaN,
    confidence: 0,
    skipped: true,
    skipReason: reason,
  };
}

export function predictRealtimeDrop(params: {
  series: RoastingSeries;
  pastProfiles: PastProfile[];
  pastRoastingSeries: RoastingSeries[];
  predictHistory: Record<number, PredictRealtimeDropResult>;
  targetLoss: number | undefined;
  weights?: KNNDistanceWeights;
  similarityIntervalSec?: number;
  forceRankSimilarity?: boolean;
  useClosestProfile?: boolean;
}): PredictRealtimeDropResult {
  const {
    series,
    pastProfiles,
    pastRoastingSeries,
    predictHistory,
    targetLoss,
    weights,
    similarityIntervalSec = 10,
    forceRankSimilarity = false,
    useClosestProfile = false,
  } = params;
  /* ───────────────── 기본 방어 ───────────────── */

  if (!series.dryEndTime || series.series.length < 4) {
    return { ...skippedResult("invalid_series") };
  }

  /* ───────────────── BT 곡선 유사도 (UI 전용) ───────────────── */
  const closestSeries = findMostSimilarBTCurve(series, pastRoastingSeries);

  if (targetLoss === undefined) {
    return { ...skippedResult("loss_undefiend"), closestSeries };
  }

  const lastPoint = series.series.at(-1)!;
  const nowTime = lastPoint.time;

  if (
    isNaN(nowTime) ||
    series.dryEndTime < 0 ||
    nowTime - series.dryEndTime < 0 ||
    nowTime - series.dryEndTime < MIN_DEV_ELAPSED_SEC
  ) {
    return { ...skippedResult("dev_too_early"), closestSeries };
  }

  /* ───────────────── 현재 상태 ───────────────── */

  const state = buildCurrentStateFromSeries(series);

  const { avgDevTime, avgFinalLoss } = knnAggregateWeighted(state, pastProfiles, 3, weights);

  const currLoss = estimateCurrentLoss(state.tDev, avgDevTime, avgFinalLoss);

  /* ───────────────── 이전 예측 탐색 ───────────────── */
  //현재 loss_interval_too_short_prevEntry 무한 루프로 계산이 제대로 되지 않음.

  const prevEntry = findPreviousPrediction(predictHistory, nowTime);

  // if (!prevEntry) {
  //   return {
  //     ...skippedResult("loss_interval_too_short_prevEntry"),
  //     currLoss,
  //     closestSeries,
  //   };
  // }

  const { time: prevTime, result: prevResult } = prevEntry ?? { time: 0 };
  const deltaTime = nowTime - prevTime;

  if (deltaTime < MIN_LOSS_INTERVAL_SEC) {
    return {
      ...skippedResult("loss_interval_too_short_deltaTime"),
      currLoss,
      closestSeries,
    };
  }

  /* ───────────────── loss slope ───────────────── */

  const lossSlopMulti = computeMultiHistorySlope(
    predictHistory,
    nowTime,
    15, // windowSec
  );

  // ── loss slope
  const prev = lossSlopMulti ? undefined : findPreviousPrediction(predictHistory, nowTime);
  const lossSlope = lossSlopMulti ?? (prev ? computeLossSlope(currLoss, prev.result.currLoss, deltaTime) : undefined);

  if (!lossSlope) {
    return {
      ...skippedResult("loss_interval_too_short_lossSlope"),
      currLoss,
      currTime: nowTime,
      closestSeries,
    };
  }
  const prevSlope = computeMultiHistorySlope(predictHistory, nowTime - 5, 15);
  /* ───────────────── drop 예측 (base) ───────────────── */

  // ── drop 예측
  const basePrediction = extrapolateDropTime(state, currLoss, lossSlope, targetLoss);

  /* ───────────────── 유사도 기반 보조 정보 ───────────────── */

  let closestProfile: RankedProfile | undefined;

  const shouldRank = forceRankSimilarity || Math.floor(state.tDev) % similarityIntervalSec === 0;

  if (shouldRank) {
    const recentRORSeries = extractRecentRORFromSeries(series, similarityIntervalSec);

    closestProfile = rankPastProfiles(state, pastProfiles, 1, DEFAULT_SIMILARITY_FEATURE_WEIGHTS, recentRORSeries)[0];
  }

  /* ───────────────── 예측 안정화 (선택) ───────────────── */

  let finalPrediction = basePrediction;

  if (useClosestProfile && closestProfile && closestProfile.distance < 0.8) {
    finalPrediction = {
      remainingTime: 0.85 * basePrediction.remainingTime + 0.15 * (closestProfile.profile.devTime - state.tDev),
      predictedDropBT: 0.85 * basePrediction.predictedDropBT + 0.15 * closestProfile.profile.dropBT,
    };
  }

  /* ───────────────── confidence 계산 ───────────────── */
  const currSlope = lossSlope;

  const confidence = computePredictionConfidence({
    currSlope,
    prevSlope,
    deltaTime: similarityIntervalSec,
    tDev: state.tDev,
    similarityDistance: closestProfile?.distance,
  });
  /* ───────────────── 최종 반환 ───────────────── */

  return {
    ...finalPrediction,
    currTime: nowTime,
    currLoss,
    confidence,
    closestProfile,
    closestSeries,
    skipped: false,
  };
}

export function findLatestPredictionBefore(
  history: Record<number, PredictRealtimeDropResult>,
  nowTime: number,
): { time: number; result: PredictRealtimeDropResult } | undefined {
  let latestTime = -Infinity;
  let latestResult: PredictRealtimeDropResult | undefined;

  for (const tStr in history) {
    const t = Number(tStr);
    if (t < nowTime && t > latestTime) {
      latestTime = t;
      latestResult = history[t];
    }
  }

  return latestResult ? { time: latestTime, result: latestResult } : undefined;
}

function extractRecentRORFromSeries(series: RoastingSeries, windowSec: number): number[] {
  const points = series.series;
  if (points.length < 2) return [];

  const endTime = points.at(-1)!.time;
  const startTime = Math.max(endTime - windowSec, 0);

  const window = points.filter((p) => p.time >= startTime && p.time <= endTime);

  const ror: number[] = [];
  for (let i = 1; i < window.length; i++) {
    const dt = window[i].time - window[i - 1].time;
    if (dt > 0) {
      ror.push((window[i].bt - window[i - 1].bt) / dt);
    }
  }

  return ror;
}
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function lossSlopeStabilityScore(currSlope: number, prevSlope?: number): number {
  if (currSlope <= 0) return 0;
  if (prevSlope == null || prevSlope <= 0) return 0.4;

  const diffRatio = Math.abs(currSlope - prevSlope) / prevSlope;
  return clamp(1 - diffRatio, 0, 1);
}
function deltaTimeScore(deltaTime: number): number {
  const IDEAL = 10;
  const MAX = 30;
  return clamp((deltaTime - 3) / (IDEAL - 3), 0, 1);
}
function devProgressScore(tDev: number): number {
  if (tDev < 30) return tDev / 30;
  return 1;
}
function similarityScore(distance?: number): number {
  if (distance == null) return 0.5;
  if (distance >= 1) return 0;
  return 1 - distance;
}

export function computePredictionConfidence(params: {
  currSlope: number;
  prevSlope?: number;
  deltaTime: number;
  tDev: number;
  similarityDistance?: number;
}): number {
  const a = lossSlopeStabilityScore(params.currSlope, params.prevSlope);
  const b = deltaTimeScore(params.deltaTime);
  const c = devProgressScore(params.tDev);
  const d = similarityScore(params.similarityDistance);

  return clamp(0.4 * a + 0.25 * b + 0.2 * c + 0.15 * d, 0, 1);
}
